import { HEALTH, ITEMS, count, isItemId, toolCondition, type Inventory, type ItemId, type Wear } from "@dyson/shared";
import { DEFAULT_KEYS, keyLabel, type Action } from "../input/bindings";
import { drawItemIcon } from "../render/TileAtlas";

/** Slots in the quick-access bar (keys 1-9). */
export const QUICKBAR_SIZE = 9;

export interface HudState {
  name: string;
  /** What the player is doing right now, with the relevant keys. */
  mode: string;
  /** Why the hovered dig or build would be refused, if it would. */
  hint: string;
  speed: number;
  /** False while the turret we're manning reloads. */
  reloaded: boolean;
  engines: number;
  turrets: number;
  tiles: number;
  /** Fuel and water in the ship you are aboard, 0..1 of what its furnaces hold, or null with no furnace. */
  fuel: number | null;
  water: number | null;
  /** Our health, 0..HEALTH.max. */
  health: number;
  /** Steam left in our steampack, 0..1; null when we aren't wearing one (the gauge hides). */
  steam: number | null;
  /** How worn our tools are, for the bar under each tool slot. */
  wear: Wear;
  inventory: Inventory;
  /** What's in each quick-access slot. */
  quickbar: (ItemId | null)[];
  selected: number;
  online: number;
  pingMs: number;
  fps: number;
  status: string;
  /** The ship's flight readout, shown under its stats; empty hides it. */
  readout: string;
  /** Inventory stacks used, of how many we can carry (INVENTORY.slots). */
  pack: { used: number; max: number };
  /** A label by the mouse (a sign's text, on hover), in client px. */
  tooltip: { text: string; x: number; y: number } | null;
  /** Photo mode: everything but a fading hint is hidden. */
  photo: boolean;
}

/** Below this share of HEALTH.max the screen edges pulse red until you heal. */
const LOW_HEALTH = 0.3;

export class Hud {
  onSelect: (index: number) => void = () => {};
  /** An item was dropped on a slot (dragged from the inventory). */
  onAssign: (index: number, item: ItemId) => void = () => {};
  /** A slot was right-clicked to empty it. */
  onClear: (index: number) => void = () => {};
  /** One slot was dragged onto another. */
  onSwap: (from: number, to: number) => void = () => {};

  private readonly root = document.getElementById("hud")!;
  private readonly name = document.getElementById("hud-name")!;
  private readonly shipInfo = document.getElementById("hud-ship")!;
  private readonly speed = document.getElementById("hud-speed")!;
  private readonly online = document.getElementById("hud-online")!;
  private readonly ping = document.getElementById("hud-ping")!;
  private readonly mode = document.getElementById("hud-mode")!;
  private readonly hint = document.getElementById("hud-hint")!;
  private readonly hotbar = document.getElementById("hud-hotbar")!;
  private readonly status = document.getElementById("hud-status")!;
  private readonly slots: HTMLElement[] = [];
  private readonly icons: HTMLCanvasElement[] = [];
  private readonly counts: HTMLElement[] = [];
  private readonly conditions: HTMLElement[] = [];
  private readonly health = document.getElementById("hud-health-fill")!;
  private readonly steam = document.getElementById("hud-steam-fill")!;
  private readonly hurt = document.getElementById("hurt")!;
  private readonly lowHealth = document.getElementById("low-health")!;
  private readonly readout = document.getElementById("hud-readout")!;
  private readonly pack = document.getElementById("hud-pack")!;
  private readonly tooltip = document.getElementById("hud-tooltip")!;
  private readonly photoHint = document.getElementById("photo-hint")!;
  private readonly help = document.getElementById("hud-help")!;
  private readonly helpToggle = document.getElementById("hud-help-toggle")!;
  /** Photo mode as last drawn, so its hint only replays on the way in. */
  private photo = false;
  /** What each slot's icon currently shows, so icons are only redrawn when it changes. */
  private shown: (ItemId | null | undefined)[] = [];
  /** The key bindings the help was last written from, and whether touch controls are up (the help describes those instead). */
  private keys: Record<Action, string[]> = DEFAULT_KEYS;
  private touch = false;

  constructor() {
    // The controls stay out of the way behind a ? (bottom left) until asked for.
    this.helpToggle.addEventListener("click", () => {
      const open = this.help.classList.toggle("hidden") === false;
      this.helpToggle.classList.toggle("open", open);
      this.helpToggle.setAttribute("aria-expanded", String(open));
      this.helpToggle.blur();
    });
    for (let i = 0; i < QUICKBAR_SIZE; i++) {
      const slot = document.createElement("button");
      slot.className = "slot";
      const icon = document.createElement("canvas");
      icon.width = icon.height = 32;
      const key = document.createElement("span");
      key.className = "key";
      key.textContent = String(i + 1);
      const n = document.createElement("span");
      n.className = "count";
      // What's left of a tool, as a bar along the bottom of its slot.
      const condition = document.createElement("span");
      condition.className = "condition";
      slot.append(icon, key, n, condition);
      slot.addEventListener("click", () => this.onSelect(i));
      // Drag a slot onto another to swap them, or into the open inventory to take it off the bar.
      slot.draggable = true;
      slot.addEventListener("dragstart", (e) => {
        if (!this.shown[i]) return e.preventDefault();
        e.dataTransfer?.setData("text/plain", `slot:${i}`);
      });
      slot.addEventListener("contextmenu", (e) => {
        e.preventDefault();
        // A long press on a touch screen is one too: don't empty the slot for that.
        if (!this.touch) this.onClear(i);
      });
      // Drop target for items dragged out of the inventory panel, or another slot.
      slot.addEventListener("dragover", (e) => {
        e.preventDefault();
        slot.classList.add("drop");
      });
      slot.addEventListener("dragleave", () => slot.classList.remove("drop"));
      slot.addEventListener("drop", (e) => {
        e.preventDefault();
        slot.classList.remove("drop");
        const data = e.dataTransfer?.getData("text/plain") ?? "";
        const from = slotFromDrag(data);
        if (from !== null) {
          if (from !== i) this.onSwap(from, i);
        } else if (isItemId(data)) this.onAssign(i, data);
      });
      this.slots.push(slot);
      this.icons.push(icon);
      this.counts.push(n);
      this.conditions.push(condition);
      this.hotbar.append(slot);
    }
    this.setKeys(DEFAULT_KEYS);
  }

  /** Touch controls are up (or down): the help describes them, and the layout makes room (see #hud.touch). */
  setTouch(on: boolean): void {
    if (on === this.touch) return;
    this.touch = on;
    this.root.classList.toggle("touch", on);
    this.setKeys(this.keys);
  }

  /** Open or shut the controls help (the ? button, or touch's). */
  toggleHelp(): void {
    this.helpToggle.click();
  }

  /** Rewrite the help panel (bottom left) from the current key bindings (or, on touch, from the touch controls). */
  setKeys(keys: Record<Action, string[]>): void {
    this.keys = keys;
    if (this.touch) {
      this.help.innerHTML = [
        "Left thumb: the stick walks (tilt up to jump or climb, down to climb down; wearing a steampack it flies you in open space) &middot; Jump also climbs; hold it in the air to fly on a steampack &middot; Grab: take hold of or let go of a vine &middot; holding the grapple, a placing tap throws or releases it",
        "A finger on the world aims. Dig / Place switches what it does: hold to dig (or swing your tool), tap to place the selected block (or, close by, take the helm or a turret, open a hatch or chest, feed a furnace; or put out a fire holding water) &middot; Rotate turns what you're placing &middot; tap a slot to pick it &middot; Bag: inventory &amp; crafting (tap an item to put it in the selected slot)",
        "Helm: stick up/down lifts and sinks, left/right backs and goes forward &middot; ↺ ↻ turn &middot; Fire: bow guns &middot; Turret: drag to aim, Fire fires",
        "Two fingers zoom &middot; tap the map to open it, tap it to teleport &middot; Eat heals &middot; the gear (top right) has settings",
      ].join("<br />");
      return;
    }
    const k = (a: Action) => {
      // Arrows are listed only when nothing else is bound, so "A/D walk" doesn't become "A/←/D/→ walk".
      const all = [...new Set(keys[a].map(keyLabel))];
      const plain = [...new Set(keys[a].filter((c) => !c.startsWith("Arrow")).map(keyLabel))];
      return escapeHtml((plain.length ? plain : all).join("/") || "unbound");
    };
    this.help.innerHTML = [
      `${k("left")}/${k("right")} walk &middot; ${k("up")} jump &amp; climb (double jump on rock to launch into space) &middot; move near a vine to take hold and travel along it (${k("hook")} lets go) &middot; aboard a ship it's zero-g: hold on and crawl along the floor, walls and ceiling, push away to kick off &middot; hold ${k("up")} in the air to fly on a steampack, wearing one (free in open space: just push, ${k("turnLeft")}/${k("turnRight")} spin) &middot; ${k("eat")} eat`,
      `Helm (right click it, standing there): ${k("up")} up &middot; ${k("down")} down &middot; ${k("left")} back &middot; ${k("right")} forward &middot; ${k("turnLeft")}/${k("turnRight")} turn &middot; right click leaves &middot; Turret (right click it from its seat): mouse aims, click fires`,
      `Hold left click dig &middot; right click place, or close by: open a hatch or chest, feed a furnace, sleep in a bed (and put out a fire holding water) &middot; ${k("rotate")} rotate &middot; Ctrl+${k("undo")} undo &middot; 1&ndash;9 pick &middot; ${k("inventory")} inventory &amp; crafting &middot; ${k("hook")} grapple (with nothing to grab)`,
      `${k("camera")} camera &middot; ${k("mute")} sound &middot; ${k("photo")} photo &middot; ${k("settings")} settings &middot; scroll zoom &middot; ${k("respawn")} start over (your bed, or a rock at the start) &middot; click the map to open it &middot; Enter chat (/help for commands)`,
    ].join("<br />");
  }

  /** Flash the screen edges red: we just got hurt. */
  flashHurt(): void {
    this.hurt.classList.remove("on");
    void this.hurt.offsetWidth; // restart the fade
    this.hurt.classList.add("on");
  }

  show(): void {
    this.root.classList.remove("hidden");
  }

  hide(): void {
    this.root.classList.add("hidden");
  }

  update(s: HudState): void {
    this.name.textContent = s.name;
    const health = Math.max(0, s.health) / HEALTH.max;
    this.health.style.width = `${Math.round(health * 100)}%`;
    this.steam.parentElement!.classList.toggle("hidden", s.steam === null);
    this.steam.style.width = `${Math.round((s.steam ?? 0) * 100)}%`;
    this.steam.classList.toggle("empty", (s.steam ?? 0) < 0.05);
    this.health.classList.toggle("low", health < LOW_HEALTH);
    // Pulses for as long as you're low, not just the moment you're hit (that's the hurt flash).
    this.lowHealth.classList.toggle("on", health < LOW_HEALTH);
    const fuel = s.fuel === null || s.water === null ? " · no furnace" : ` · fuel ${Math.round(s.fuel * 100)}% · water ${Math.round(s.water * 100)}%`;
    this.shipInfo.textContent = s.tiles ? `Ship: ${s.tiles} tiles · ${s.engines} engines · ${s.turrets} turrets${fuel}` : "Not aboard a ship";
    this.shipInfo.classList.toggle("warn", s.fuel !== null && s.water !== null && Math.min(s.fuel, s.water) < 0.15);
    this.speed.textContent = `${s.speed.toFixed(0)} m/s${s.reloaded ? "" : " · reloading"}`;
    this.online.textContent = `${s.online} aboard the sphere`;
    this.ping.textContent = `${s.pingMs.toFixed(0)} ms ping · ${s.fps.toFixed(0)} fps`;
    this.mode.textContent = s.mode;
    this.hint.textContent = s.hint;
    this.slots.forEach((slot, i) => {
      const item = s.quickbar[i] ?? null;
      if (this.shown[i] !== item) {
        this.shown[i] = item;
        if (item) drawItemIcon(item, this.icons[i]!);
        else this.icons[i]!.getContext("2d")!.clearRect(0, 0, 32, 32);
        slot.title = item ? `${ITEMS[item].name} (right click to empty)` : "Empty: drag something here from your inventory (I)";
      }
      const n = item ? count(s.inventory, item) : 0;
      slot.classList.toggle("selected", i === s.selected);
      slot.classList.toggle("empty", n === 0);
      this.counts[i]!.textContent = n ? String(n) : "";
      const condition = item && n && ITEMS[item].tier ? toolCondition(item, s.wear) : null;
      this.conditions[i]!.style.display = condition === null ? "none" : "block";
      if (condition !== null) {
        this.conditions[i]!.style.width = `${Math.round(condition * 100)}%`;
        this.conditions[i]!.classList.toggle("low", condition < 0.25);
      }
    });
    this.status.textContent = s.status;

    this.readout.textContent = s.readout;
    this.readout.classList.toggle("hidden", !s.readout);
    this.pack.textContent = `pack ${s.pack.used}/${s.pack.max}`;
    this.pack.classList.toggle("warn", s.pack.used >= s.pack.max);

    this.tooltip.classList.toggle("hidden", !s.tooltip);
    if (s.tooltip) {
      if (this.tooltip.textContent !== s.tooltip.text) this.tooltip.textContent = s.tooltip.text;
      this.tooltip.style.transform = `translate(${Math.round(s.tooltip.x + 14)}px, ${Math.round(s.tooltip.y + 18)}px)`;
    }

    if (s.photo !== this.photo) {
      this.photo = s.photo;
      this.root.classList.toggle("photo", s.photo);
      // Replay the hint's fade each time photo mode starts.
      this.photoHint.classList.remove("on");
      if (s.photo) {
        void this.photoHint.offsetWidth;
        this.photoHint.classList.add("on");
      }
    }
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => `&#${c.charCodeAt(0)};`);
}

/** The quick-access slot a drag started from ("slot:3"), or null if it came from the inventory. */
export function slotFromDrag(data: string): number | null {
  const m = /^slot:(\d+)$/.exec(data);
  return m ? Number(m[1]) : null;
}
